import { useEffect, useState, useCallback } from 'react';
import api, { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Users, Loader2, Plus, KeyRound, Trash2, Power, ShieldCheck, AlertTriangle, Shield } from 'lucide-react';
import { toast } from 'sonner';

const PERM_GROUPS = [
  { res: 'structure', l: 'Structure', d: 'Societies, towers, lifts, sites', actions: ['view', 'edit', 'delete'] },
  { res: 'campaigns', l: 'Campaigns', d: 'Campaigns & artwork', actions: ['view', 'edit', 'delete'] },
  { res: 'inspections', l: 'Inspections', d: 'Inspection rounds', actions: ['view', 'edit', 'delete'] },
  { res: 'billing', l: 'Billing', d: 'Rent, ledger, status', actions: ['view', 'edit'] },
  { res: 'users', l: 'Users', d: 'The User Master', actions: ['view', 'edit', 'delete'] },
];
const SINGLE_PERMS = [
  { k: 'field_capture', l: 'Field Capture', d: 'Capture proofs, flag, repair' },
  { k: 'reports_view', l: 'View & Generate Reports', d: 'Dashboards, PDF/Excel' },
  { k: 'delete_database', l: 'Delete Database', d: 'One-time full wipe' },
];
const ACTION_LABEL = { view: 'View', edit: 'Edit', delete: 'Delete' };

const ROLES = [
  { v: 'operator', l: 'Operator' }, { v: 'manager', l: 'Manager' },
  { v: 'client', l: 'Client' }, { v: 'society_manager', l: 'Society Manager' },
  { v: 'billing', l: 'Billing' }, { v: 'admin', l: 'Admin' },
];
const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.v, r.l]));
const ROLE_CLS = {
  operator: 'bg-sky-100 text-sky-800 border-sky-300', manager: 'bg-violet-100 text-violet-800 border-violet-300',
  client: 'bg-emerald-100 text-emerald-800 border-emerald-300', society_manager: 'bg-amber-100 text-amber-800 border-amber-300',
  billing: 'bg-orange-100 text-orange-800 border-orange-300', admin: 'bg-rose-100 text-rose-800 border-rose-300',
};

export default function UserMaster() {
  const [users, setUsers] = useState(null);
  const [wipeAvail, setWipeAvail] = useState(false);
  const load = useCallback(() => {
    api.get('/auth/admin/users').then((r) => setUsers(r.data)).catch(() => setUsers([]));
    api.get('/auth/admin/wipe-status').then((r) => setWipeAvail(r.data.available)).catch(() => setWipeAvail(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!users) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="overline flex items-center gap-2 text-primary"><Users className="h-3.5 w-3.5" /> User Master</p>
          <h1 className="font-head text-3xl font-bold">Staff, Clients &amp; Societies</h1>
          <p className="text-sm text-muted-foreground">Create logins and manage every account. Admin logins are protected by an emailed code.</p>
        </div>
        <NewUserDialog onCreated={load} />
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-secondary/40 text-left text-xs uppercase text-muted-foreground">
            <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-border/60 last:border-0">
                <td className="p-3 font-semibold">{u.name} {u.role === 'admin' && <ShieldCheck className="ml-1 inline h-3.5 w-3.5 text-rose-500" />}</td>
                <td className="p-3 text-muted-foreground">{u.email}</td>
                <td className="p-3"><span className={`rounded-full border px-2.5 py-0.5 text-xs font-bold ${ROLE_CLS[u.role] || 'bg-slate-100 text-slate-700 border-slate-300'}`}>{ROLE_LABEL[u.role] || u.role}</span></td>
                <td className="p-3">
                  <span data-testid={`user-status-${u.id}`} className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${u.active === false ? 'bg-slate-200 text-slate-600' : 'bg-emerald-100 text-emerald-800'}`}>
                    {u.active === false ? 'Deactivated' : 'Active'}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    <PermsDialog user={u} onDone={load} />
                    <ResetPwDialog user={u} />
                    <ToggleActive user={u} onDone={load} />
                    <DeleteUser user={u} onDone={load} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {wipeAvail && <DangerZone onWiped={load} />}
    </div>
  );
}

function DangerZone({ onWiped }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const wipe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/auth/admin/wipe-database', { confirm });
      toast.success(`Database wiped — ${data.collections_cleared.length} collections cleared`);
      setOpen(false); setConfirm(''); onWiped();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Failed to wipe'); }
    setBusy(false);
  };
  return (
    <div className="rounded-xl border-2 border-rose-300 bg-rose-50/60 p-5">
      <p className="overline flex items-center gap-2 text-rose-600"><AlertTriangle className="h-3.5 w-3.5" /> Danger Zone</p>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-head text-lg font-bold text-rose-800">Delete the entire database</h3>
          <p className="max-w-xl text-sm text-rose-700/80">Permanently erases all societies, towers, lifts, sites, campaigns, inspections, proofs, photos, billing and non-admin accounts. Admin logins are kept. This button can be used <strong>only once</strong> and then disappears forever.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="destructive" data-testid="wipe-db-btn" className="shrink-0"><Trash2 className="mr-1 h-4 w-4" /> Delete Database</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-head text-2xl text-rose-700">Delete everything?</DialogTitle>
              <DialogDescription>This is irreversible and can only be done once. Type <strong>DELETE</strong> below to confirm.</DialogDescription>
            </DialogHeader>
            <Input data-testid="wipe-confirm-input" placeholder="Type DELETE" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            <DialogFooter>
              <Button variant="destructive" disabled={busy || confirm !== 'DELETE'} onClick={wipe} data-testid="wipe-confirm-btn">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Permanently Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function NewUserDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'operator' });
  const [saving, setSaving] = useState(false);
  const create = async () => {
    if (!form.name || !form.email || !form.password) return toast.error('Name, email and password are required');
    setSaving(true);
    try {
      await api.post('/auth/admin/users', form);
      toast.success('Account created');
      setOpen(false); setForm({ name: '', email: '', password: '', role: 'operator' }); onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Failed to create'); }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-user-btn"><Plus className="mr-1 h-4 w-4" /> New Account</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head text-2xl">New Account</DialogTitle><DialogDescription>Create a login for staff, a client or a society. Share the email + password with them.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Email</Label><Input type="email" data-testid="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>Role</Label>
            <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-border bg-background px-3 text-sm">
              {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </div>
          <div><Label>Password</Label><Input data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={create} disabled={saving} data-testid="save-user-btn">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PermsDialog({ user, onDone }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(new Set(user.permissions || []));
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setSel(new Set(user.permissions || [])); }, [open, user.permissions]);
  if (user.role === 'admin') {
    return <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-400" disabled title="Admins always have all permissions"><Shield className="h-4 w-4" /></Button>;
  }
  const toggle = (k) => setSel((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/auth/admin/users/${user.id}/permissions`, { permissions: [...sel] });
      toast.success('Permissions updated'); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Failed'); }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8 text-primary" data-testid={`perms-${user.id}`} title="Permissions"><Shield className="h-4 w-4" /></Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head text-xl">Permissions — {user.name}</DialogTitle><DialogDescription>Tick exactly what this {ROLE_LABEL[user.role] || user.role} can do. These override the role defaults.</DialogDescription></DialogHeader>
        <div className="max-h-[60vh] space-y-2 overflow-y-auto">
          {PERM_GROUPS.map((g) => (
            <div key={g.res} data-testid={`perm-group-${user.id}-${g.res}`} className="rounded-lg border border-border p-2.5">
              <div className="mb-1.5"><span className="block text-sm font-bold">{g.l}</span><span className="block text-xs text-muted-foreground">{g.d}</span></div>
              <div className="flex flex-wrap gap-2">
                {g.actions.map((a) => {
                  const k = `${g.res}_${a}`;
                  return (
                    <label key={k} data-testid={`perm-${user.id}-${k}`} className={`flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm ${sel.has(k) ? 'border-primary bg-primary/10 font-semibold text-primary' : 'border-border'}`}>
                      <input type="checkbox" className="h-4 w-4 accent-[--primary]" checked={sel.has(k)} onChange={() => toggle(k)} data-testid={`perm-cb-${user.id}-${k}`} />
                      {ACTION_LABEL[a]}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
          {SINGLE_PERMS.map((p) => (
            <label key={p.k} data-testid={`perm-${user.id}-${p.k}`} className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-2.5 hover:bg-secondary/40">
              <input type="checkbox" className="mt-1 h-4 w-4 accent-[--primary]" checked={sel.has(p.k)} onChange={() => toggle(p.k)} data-testid={`perm-cb-${user.id}-${p.k}`} />
              <span><span className="block text-sm font-bold">{p.l}</span><span className="block text-xs text-muted-foreground">{p.d}</span></span>
            </label>
          ))}
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} data-testid={`perms-save-${user.id}`}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Permissions</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPwDialog({ user }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState('');
  const [saving, setSaving] = useState(false);
  const reset = async () => {
    if (pw.length < 4) return toast.error('Password too short');
    setSaving(true);
    try { await api.post(`/auth/admin/users/${user.id}/reset-password`, { password: pw }); toast.success('Password reset'); setOpen(false); setPw(''); }
    catch { toast.error('Failed'); }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`reset-pw-${user.id}`} title="Reset password"><KeyRound className="h-4 w-4" /></Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head text-xl">Reset password — {user.name}</DialogTitle><DialogDescription>Type a new password for this login.</DialogDescription></DialogHeader>
        <Input data-testid={`reset-pw-input-${user.id}`} placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} />
        <DialogFooter><Button onClick={reset} disabled={saving} data-testid={`reset-pw-save-${user.id}`}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Reset</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ToggleActive({ user, onDone }) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try { await api.patch(`/auth/admin/users/${user.id}/active`, { active: user.active === false }); toast.success(user.active === false ? 'Reactivated' : 'Deactivated'); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Failed'); }
    setBusy(false);
  };
  return (
    <Button size="icon" variant="ghost" className={`h-8 w-8 ${user.active === false ? 'text-emerald-500' : 'text-amber-500'}`} data-testid={`toggle-active-${user.id}`} disabled={busy} onClick={toggle} title={user.active === false ? 'Reactivate' : 'Deactivate'}>
      <Power className="h-4 w-4" />
    </Button>
  );
}

function DeleteUser({ user, onDone }) {
  const del = async () => {
    if (!window.confirm(`Delete account ${user.name} (${user.email})? This cannot be undone.`)) return;
    try { await api.delete(`/auth/admin/users/${user.id}`); toast.success('Account deleted'); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Failed'); }
  };
  return <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-400" data-testid={`delete-user-${user.id}`} onClick={del} title="Delete"><Trash2 className="h-4 w-4" /></Button>;
}
